

import { useState, useEffect } from "react";
import { mastersApi } from "@/lib/api";
import { formatDate, formatMonthYear } from "@/lib/dateFormat";

interface DatePickerWithAvailabilityProps {
  value: string;
  onChange: (date: string) => void;
  venueId?: number;
  label?: string;
  error?: string;
  disabled?: boolean;
}

interface AvailabilityData {
  booked_dates: Array<{
    date: string;
    event_name: string;
    client_name: string;
  }>;
  venue_name?: string;
}

// Builds a YYYY-MM-DD string from year/month(0-based)/day without any UTC conversion.
// Using toISOString() on new Date(y, m, d) shifts the date back one day in IST (UTC+5:30).
const localDateStr = (year: number, month: number, day: number): string =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

export default function DatePickerWithAvailability({
  value,
  onChange,
  venueId,
  label = "Event Date",
  error,
  disabled = false,
}: DatePickerWithAvailabilityProps) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState<AvailabilityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  useEffect(() => {
    if (venueId) {
      fetchAvailability();
    }
  }, [venueId, currentMonth]);

  const fetchAvailability = async () => {
    if (!venueId) return;

    try {
      setLoading(true);
      const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      const fromDate = localDateStr(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate());
      const toDate = localDateStr(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate());

      const response = await mastersApi.venues.getAvailability(venueId, fromDate, toDate);
      setAvailability(response.data?.data || null);
    } catch (error) {
      console.error("Failed to fetch availability", error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateBooked = (day: number): boolean => {
    if (!availability?.booked_dates) return false;
    const dateStr = localDateStr(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return availability.booked_dates.some((bd) => bd.date === dateStr);
  };

  const getBookedEventInfo = (day: number) => {
    if (!availability?.booked_dates) return null;
    const dateStr = localDateStr(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return availability.booked_dates.find((bd) => bd.date === dateStr);
  };

  const handleDateClick = (day: number) => {
    if (isDateBooked(day)) return;

    const dateStr = localDateStr(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    onChange(dateStr);
    setShowCalendar(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDay }, (_, i) => i);

  const monthName = formatMonthYear(currentMonth);
  const selectedDateObj = value ? new Date(value) : null;

  return (
    <div className="relative">
      <label className="block text-xs font-medium text-stone-600 mb-1.5">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={value ? formatDate(value) : ""}
          onClick={() => setShowCalendar(!showCalendar)}
          readOnly
          disabled={disabled}
          placeholder="Select date"
          className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors cursor-pointer outline-none ${
            error
              ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
              : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
          } ${disabled ? "bg-stone-100 cursor-not-allowed" : ""}`}
        />
        <svg
          className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>

      {error && <p className="text-xs text-rose-500 mt-1">{error}</p>}

      {showCalendar && (
        <div className="absolute top-full left-0 mt-2 bg-white border border-stone-200 rounded-lg shadow-lg p-4 z-50 w-80">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePrevMonth}
              className="p-1 hover:bg-stone-100 rounded transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="text-sm font-semibold text-stone-700">{monthName}</h3>
            <button
              onClick={handleNextMonth}
              className="p-1 hover:bg-stone-100 rounded transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Venue Info */}
          {availability?.venue_name && (
            <div className="mb-3 p-2 bg-blue-50 rounded text-xs text-blue-700">
              <p className="font-semibold">{availability.venue_name}</p>
            </div>
          )}

          {/* Legend */}
          <div className="mb-3 flex gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-red-100 border border-red-300"></div>
              <span>Booked</span>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 mb-3">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div key={day} className="text-center text-xs font-semibold text-stone-600 py-1">
                {day}
              </div>
            ))}

            {emptyDays.map((_, i) => (
              <div key={`empty-${i}`}></div>
            ))}

            {days.map((day) => {
              const isBooked = isDateBooked(day);
              const isSelected =
                selectedDateObj?.getDate() === day &&
                selectedDateObj?.getMonth() === currentMonth.getMonth() &&
                selectedDateObj?.getFullYear() === currentMonth.getFullYear();
              const bookedInfo = getBookedEventInfo(day);
              const isPast = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day) < new Date();

              return (
                <div
                  key={day}
                  className="relative"
                  onMouseEnter={() => bookedInfo && setHoveredDate(`${day}`)}
                  onMouseLeave={() => setHoveredDate(null)}
                >
                  <button
                    onClick={() => handleDateClick(day)}
                    disabled={isBooked || isPast}
                    className={`w-full aspect-square rounded text-xs font-medium transition-colors flex items-center justify-center relative ${
                      isSelected
                        ? "bg-amber-500 text-white border border-amber-600"
                        : isBooked
                          ? "bg-red-100 text-red-700 border border-red-300 cursor-not-allowed"
                          : isPast
                            ? "bg-stone-100 text-stone-400 cursor-not-allowed"
                            : "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                    }`}
                  >
                    {day}
                  </button>

                  {/* Booked Event Tooltip */}
                  {bookedInfo && hoveredDate === `${day}` && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-stone-800 text-white text-xs rounded p-2 whitespace-nowrap z-10">
                      <p className="font-semibold">{bookedInfo.event_name}</p>
                      <p className="text-stone-300">{bookedInfo.client_name}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Close Button */}
          <button
            onClick={() => setShowCalendar(false)}
            className="w-full px-3 py-2 rounded-lg bg-stone-100 text-stone-700 text-xs font-medium hover:bg-stone-200 transition-colors"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
